/* ============================================================
   senchado-box-state.js — BOX定期便 スロット状態管理（純粋関数）
   仕様: docs/spec-box-slot-ui.md（データモデル / エッジケース / マージポリシー）

   DOM に触らない純粋モジュール。ブラウザでは window.SenchadoBoxState、
   Node（Playwright ランナー内ユニットテスト）では module.exports で公開する。

   状態 JSON（localStorage["senchado_box_slots"] / customer.metafields.custom.box_draft_slots）:
   {
     "slots": [{ "slot_id": 1, "product_id": 7890, "variant_id": 12345,
                 "added_at": ISO8601, "is_locked": false }],
     "version": 1,
     "updated_at": ISO8601
   }
   ============================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.SenchadoBoxState = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var KEY = 'senchado_box_slots';
  var VERSION = 1;
  var TTL_DAYS = 30;
  var TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;
  var DEFAULT_SLOT_COUNT = 3;
  var MERGE_WINDOW_MS = 5 * 60 * 1000; // マージ競合とみなす updated_at 近接幅（spec エッジケース5）
  var MAX_PRESELECT = 10;

  function toTime(iso) {
    var t = new Date(iso).getTime();
    return isNaN(t) ? null : t;
  }

  function createEmpty(now) {
    return {
      slots: [],
      version: VERSION,
      updated_at: new Date(now || Date.now()).toISOString()
    };
  }

  /*
   * 生データを安全な状態オブジェクトへ正規化する。
   * - version 不一致 / 形状不正 / TTL(30日) 超過 → 空状態
   * - validIds（現行商品IDの Set/Array）を渡すと、終売＝マスターに無い商品を除去（エッジケース4）
   */
  function sanitize(raw, now, validIds) {
    now = now || Date.now();
    if (!raw || typeof raw !== 'object') return createEmpty(now);
    if (raw.version !== VERSION) return createEmpty(now);
    if (!Array.isArray(raw.slots)) return createEmpty(now);

    var updated = toTime(raw.updated_at);
    if (updated === null || now - updated > TTL_MS) return createEmpty(now);

    var valid = null;
    if (validIds) {
      valid = {};
      (Array.isArray(validIds) ? validIds : Array.from(validIds)).forEach(function (id) {
        valid[Number(id)] = true;
      });
    }

    var seen = {};
    var slots = [];
    raw.slots.forEach(function (s) {
      if (!s || typeof s !== 'object') return;
      var slotId = Number(s.slot_id);
      var productId = Number(s.product_id);
      if (!Number.isInteger(slotId) || slotId < 1) return;
      if (!Number.isInteger(productId) || productId < 1) return;
      if (seen[slotId]) return;
      if (valid && !valid[productId]) return;
      seen[slotId] = true;
      slots.push({
        slot_id: slotId,
        product_id: productId,
        variant_id: Number(s.variant_id) || null,
        added_at: typeof s.added_at === 'string' ? s.added_at : new Date(now).toISOString(),
        is_locked: s.is_locked === true
      });
    });

    return { slots: slots, version: VERSION, updated_at: raw.updated_at };
  }

  /* storage は localStorage 互換（getItem/setItem）。Safari private 等の例外は握って空状態。 */
  function load(storage, now, validIds) {
    now = now || Date.now();
    try {
      var raw = storage.getItem(KEY);
      if (!raw) return createEmpty(now);
      return sanitize(JSON.parse(raw), now, validIds);
    } catch (e) {
      return createEmpty(now);
    }
  }

  function save(storage, state, now) {
    var iso = new Date(now || Date.now()).toISOString();
    try {
      storage.setItem(KEY, JSON.stringify({
        slots: state.slots,
        version: VERSION,
        updated_at: iso
      }));
      state.updated_at = iso; // 書き込み成功時のみ更新（失敗時に in-memory とズラさない）
      return true;
    } catch (e) {
      return false;
    }
  }

  function clearStorage(storage) {
    try { storage.removeItem(KEY); } catch (e) { /* noop */ }
  }

  function getSlot(state, slotId) {
    for (var i = 0; i < state.slots.length; i++) {
      if (state.slots[i].slot_id === slotId) return state.slots[i];
    }
    return null;
  }

  function fill(state, slotId, tea, now) {
    now = now || Date.now();
    state.slots = state.slots.filter(function (s) { return s.slot_id !== slotId; });
    state.slots.push({
      slot_id: slotId,
      product_id: Number(tea.product_id),
      variant_id: Number(tea.variant_id) || null,
      added_at: new Date(now).toISOString(),
      is_locked: false
    });
    state.slots.sort(function (a, b) { return a.slot_id - b.slot_id; });
    return state;
  }

  function clear(state, slotId) {
    state.slots = state.slots.filter(function (s) { return s.slot_id !== slotId; });
    return state;
  }

  function clearAll(state) {
    state.slots = [];
    return state;
  }

  function filledCount(state) {
    return state.slots.length;
  }

  function isComplete(state, slotCount) {
    return filledCount(state) >= (slotCount || DEFAULT_SLOT_COUNT);
  }

  /*
   * Huckleberry BOXページへの引き継ぎURLを組み立てる。
   * ?preselect=<product_id>,<product_id>,... （spec: Huckleberry連携）
   */
  function buildHandoffUrl(baseUrl, state) {
    if (!baseUrl) return null;
    var ids = state.slots
      .slice()
      .sort(function (a, b) { return a.slot_id - b.slot_id; })
      .map(function (s) { return s.product_id; });
    if (ids.length === 0) return baseUrl;
    var sep = baseUrl.indexOf('?') >= 0 ? '&' : '?';
    return baseUrl + sep + 'preselect=' + ids.join(',');
  }

  /*
   * location.search から指定キーの商品ID配列を取り出す汎用パーサ。
   * XSSガード（spec エッジケース8）: 数字のみを許可し、それ以外は黙って捨てる。
   * 返り値はバリデート済み integer の配列（最大 MAX_PRESELECT 件）。
   * 用途: preselect（Huckleberry BOXページ受信）/ slots（LPプリフィル・メール流入）
   */
  function parseIdListParam(search, key) {
    if (typeof search !== 'string' || search === '' || !key) return [];
    var query = search.charAt(0) === '?' ? search.slice(1) : search;
    var value = null;
    query.split('&').forEach(function (pair) {
      var eq = pair.indexOf('=');
      if (eq < 0) return;
      var k = pair.slice(0, eq);
      if (k === key && value === null) value = pair.slice(eq + 1);
    });
    if (!value) return [];
    var decoded;
    try {
      decoded = decodeURIComponent(value);
    } catch (e) {
      return []; // 不正な %エンコーディングは黙って無視（エッジケース8）
    }
    var out = [];
    decoded.split(',').forEach(function (token) {
      var t = token.trim();
      if (/^[1-9]\d{0,19}$/.test(t) && out.length < MAX_PRESELECT) out.push(Number(t));
    });
    return out;
  }

  function parsePreselect(search) {
    return parseIdListParam(search, 'preselect');
  }

  /*
   * ドラフトのマージポリシー（spec エッジケース5 / 決裁3）:
   * - どちらかが空 → 非空を採用
   * - updated_at が MERGE_WINDOW_MS 以内で両方非空 → conflict: true（呼び出し側がモーダル表示）
   * - それ以外 → 新しい方を採用
   * 契約済みデータは常に Huckleberry が正（このマージはドラフトのみが対象）。
   */
  function merge(localState, remoteState, now) {
    now = now || Date.now();
    var local = sanitize(localState, now);
    var remote = sanitize(remoteState, now);

    if (local.slots.length === 0) return { state: remote, conflict: false, source: 'remote' };
    if (remote.slots.length === 0) return { state: local, conflict: false, source: 'local' };

    var lt = toTime(local.updated_at) || 0;
    var rt = toTime(remote.updated_at) || 0;

    if (Math.abs(lt - rt) <= MERGE_WINDOW_MS) {
      return { state: local, conflict: true, source: 'local' };
    }
    return lt > rt
      ? { state: local, conflict: false, source: 'local' }
      : { state: remote, conflict: false, source: 'remote' };
  }

  /*
   * Huckleberry DOM ガード（spec エッジケース1 / 決裁2）:
   * 期待セレクタ群を検査し、見つからなかったものを返す。
   * 空配列 = 全部存在（正常）。
   */
  function checkSelectors(doc, selectors) {
    var missing = [];
    (selectors || []).forEach(function (sel) {
      var found = false;
      try { found = !!doc.querySelector(sel); } catch (e) { found = false; }
      if (!found) missing.push(sel);
    });
    return missing;
  }

  return {
    KEY: KEY,
    VERSION: VERSION,
    TTL_DAYS: TTL_DAYS,
    TTL_MS: TTL_MS,
    DEFAULT_SLOT_COUNT: DEFAULT_SLOT_COUNT,
    MERGE_WINDOW_MS: MERGE_WINDOW_MS,
    createEmpty: createEmpty,
    sanitize: sanitize,
    load: load,
    save: save,
    clearStorage: clearStorage,
    getSlot: getSlot,
    fill: fill,
    clear: clear,
    clearAll: clearAll,
    filledCount: filledCount,
    isComplete: isComplete,
    buildHandoffUrl: buildHandoffUrl,
    parseIdListParam: parseIdListParam,
    parsePreselect: parsePreselect,
    merge: merge,
    checkSelectors: checkSelectors
  };
});
